<script setup>
import BaseModal from "@/components/global/modals/BaseModal.vue";
import LoadingBlock from "@/components/global-themed/LoadingBlock.vue";
import {useMutation, useQuery, useQueryClient} from "@tanstack/vue-query";
import axios from "axios";
import DataTable from "@/components/global-themed/table/DataTable.vue";
import TableHeaderRow from "@/components/global-themed/table/TableHeaderRow.vue";
import TableHeaderCell from "@/components/global-themed/table/TableHeaderCell.vue";
import TableBody from "@/components/global-themed/table/TableBody.vue";
import TableRow from "@/components/global-themed/table/TableRow.vue";
import TableCell from "@/components/global-themed/table/TableCell.vue";
import DefaultButton from "@/components/global-themed/buttons/DefaultButton.vue";
import {errorToast, successToast} from "@/composables/useToasts";

const props = defineProps({
  action: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['close']);

const {data: generatedFixes, isPending: queryPending} = useQuery({
  queryKey: ['generated-fixes', props.action.id],
  queryFn: async () => {
    const {data} = await axios.get(`/ajax/v2/actions/${props.action.id}/generate-fix`);
    return data.data;
  },
});

const queryClient = useQueryClient();

const {mutate: generateFix, isPending: mutationPending} = useMutation({
  mutationFn: (actionId) => axios.post(`/ajax/v2/actions/${actionId}/generate-fix`),
  onSuccess: () => {
    successToast('Fix generated');
  },
  onError: () => {
    errorToast('There was an error generating your fix');
  },
  onSettled: () => {
    queryClient.invalidateQueries(['generated-fixes'])
  },
});

const {mutate: implementFix, isPending: implementingFix} = useMutation({
  mutationFn: async ({actionId, fixId}) => {
    await axios.post(`/ajax/v2/actions/${actionId}/implement-fix/${fixId}`);
  },
  onSuccess: () => {
    successToast('Fix implemented');
  },
  onError: () => {
    errorToast('There was an error implementing your fix');
  },
});

</script>

<template>
  <Teleport
    to=".tailwind"
  >
    <BaseModal
      @close="emit('close')"
    >
      <div class="tw-w-[80vw]">
        <div class="tw-text-xl tw-font-bold tw-mb-2 tw-text-grey-dark">
          Generate fix
        </div>

        <LoadingBlock v-if="queryPending || mutationPending" />

        <div v-else-if="generatedFixes">
          <DataTable>
            <TableHeaderRow class="tw-grid-cols-3">
              <TableHeaderCell>
                Input
              </TableHeaderCell>
              <TableHeaderCell>
                Output
              </TableHeaderCell>
              <TableHeaderCell />
            </TableHeaderRow>

            <TableBody
              v-if="generatedFixes"
              class="tw-max-h-[50vh]"
            >
              <TableRow
                v-for="(fix, key) in generatedFixes"
                :key="key"
              >
                <TableCell class="tw-col-span-5">
                  <pre>{{ JSON.stringify(fix.input, null, 2) }}</pre>
                </TableCell>
                <TableCell class="tw-col-span-5">
                  <pre>{{ JSON.stringify(fix.output, null, 2) }}</pre>
                </TableCell>
                <TableCell class="tw-col-span-2 tw-self-start">
                  <DefaultButton
                    :disabled="queryPending || implementingFix"
                    @click="implementFix({'actionId': action.id, 'fixId': fix.id})"
                  >
                    Implement Fix
                  </DefaultButton>
                </TableCell>
              </TableRow>
            </TableBody>
          </DataTable>
        </div>

        <div class="tw-flex tw-justify-center">
          <DefaultButton
            :disabled="queryPending || mutationPending"
            @click="generateFix(action.id)"
          >
            {{ generatedFixes ? 'Re-generate' : 'Generate' }}
          </DefaultButton>
        </div>
      </div>
    </BaseModal>
  </Teleport>
</template>
